export const DEFAULT_NON_WRAP_MAX_SUBSCRIPTION_FEE = 5;
export const DEFAULT_ONLINE_IFAST_CHARGE = 6;
export const DEFAULT_PAPER_IFAST_CHARGE = 10;

export const IFAST_CHARGE_ONLINE =
  (DEFAULT_NON_WRAP_MAX_SUBSCRIPTION_FEE * DEFAULT_ONLINE_IFAST_CHARGE) / 100;
export const IFAST_CHARGE_PAPER =
  (DEFAULT_NON_WRAP_MAX_SUBSCRIPTION_FEE * DEFAULT_PAPER_IFAST_CHARGE) / 100;
export const WRAP_IFAST_RATE_ONLINE = DEFAULT_ONLINE_IFAST_CHARGE / 100;
export const WRAP_IFAST_RATE_PAPER = DEFAULT_PAPER_IFAST_CHARGE / 100;

const normalizePercentSetting = (value, fallback) => {
  if (value === "" || value == null) return fallback;
  const num = Number(value);
  if (Number.isNaN(num) || num < 0) return fallback;
  return num;
};

export const normalizeCommissionRateSettings = (source = {}) => ({
  nonWrapMaxSubscriptionFee: normalizePercentSetting(
    source.nonWrapMaxSubscriptionFee,
    DEFAULT_NON_WRAP_MAX_SUBSCRIPTION_FEE
  ),
  onlineIfastCharge: normalizePercentSetting(
    source.onlineIfastCharge,
    DEFAULT_ONLINE_IFAST_CHARGE
  ),
  paperIfastCharge: normalizePercentSetting(
    source.paperIfastCharge,
    DEFAULT_PAPER_IFAST_CHARGE
  ),
});

export const resolveChannelIfastChargePercent = (submissionVia, rates) => {
  const settings = normalizeCommissionRateSettings(rates);
  return submissionVia === "Paper"
    ? settings.paperIfastCharge
    : settings.onlineIfastCharge;
};

export const resolveIfastChargeMinimum = (submissionVia, rates) => {
  const settings = normalizeCommissionRateSettings(rates);
  return (
    (settings.nonWrapMaxSubscriptionFee *
      resolveChannelIfastChargePercent(submissionVia, settings)) /
    100
  );
};

export const resolveWrapIfastChargeRate = (submissionVia, rates) =>
  resolveChannelIfastChargePercent(submissionVia, rates) / 100;

export const resolveNonWrapEffectiveMinimum = ({
  submissionVia,
  settingsMin,
  rates,
}) => Math.max(settingsMin ?? 0, resolveIfastChargeMinimum(submissionVia, rates));

const toFeeNumber = (value) => {
  if (value === "" || value == null || value === undefined) return null;
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
};

const roundMoney = (value) => Math.round((value + Number.EPSILON) * 100) / 100;

export const formatRateLabel = (value) => {
  const rounded = Math.round((Number(value) + Number.EPSILON) * 100) / 100;
  return String(rounded);
};

export const formatNonWrapUpfrontFeeError = (threshold, rates) => {
  const online = formatRateLabel(resolveIfastChargeMinimum("Online", rates));
  const paper = formatRateLabel(resolveIfastChargeMinimum("Paper", rates));
  return `Upfront fee / switch fee % must be at least ${formatRateLabel(
    threshold
  )}% (Online: ${online}%, Paper: ${paper}%)`;
};

const resolveIfastCharge = ({
  accountType,
  submissionVia,
  totalCharge,
  rates,
}) => {
  const settings = normalizeCommissionRateSettings(rates);
  const channelRate = resolveChannelIfastChargePercent(submissionVia, settings);
  if (accountType === "NonWrap") {
    return (settings.nonWrapMaxSubscriptionFee * channelRate) / 100;
  }
  if (accountType === "Wrap") {
    return (totalCharge * channelRate) / 100;
  }
  return null;
};

export const buildCommissionChargeSummary = ({
  submissionVia,
  commission = {},
  investmentAmount,
  rates,
} = {}) => {
  const accountType = commission.accountType;
  if (!accountType) return null;

  let totalCharge = null;
  if (accountType === "NonWrap") {
    totalCharge = toFeeNumber(commission.upfrontFee ?? commission.switchFee);
  } else if (accountType === "Wrap") {
    totalCharge = toFeeNumber(commission.upfrontWrapFee);
  }

  if (totalCharge == null) return null;

  const settings = normalizeCommissionRateSettings(rates);
  const channelIfastChargePercent = resolveChannelIfastChargePercent(
    submissionVia,
    settings
  );
  const ifastCharge = resolveIfastCharge({
    accountType,
    submissionVia,
    totalCharge,
    rates: settings,
  });
  const commissionRate = totalCharge - ifastCharge;
  const amount = toFeeNumber(investmentAmount);

  return {
    accountType,
    submissionVia: submissionVia === "Paper" ? "Paper" : "Online",
    totalCharge,
    upfrontFee: totalCharge,
    ifastCharge,
    fpCharge: commissionRate,
    commissionRate,
    commissionAmount:
      amount == null ? null : roundMoney((amount * commissionRate) / 100),
    investmentAmount: amount,
    channelIfastChargePercent,
    nonWrapMaxSubscriptionFee: settings.nonWrapMaxSubscriptionFee,
  };
};

export const formatCommissionPercent = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "—";
  const num = Number(value);
  const at3 = num.toFixed(3);
  const at2 = num.toFixed(2);
  return `${Number(at3) !== Number(at2) ? at3 : at2}%`;
};

export const formatGroupedAmount = (value) => {
  const negative = Number(value) < 0;
  const [whole, frac] = Math.abs(Number(value)).toFixed(2).split(".");
  const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${negative ? "-" : ""}${withCommas}.${frac}`;
};

export const buildCommissionFormulaLines = (summary) => {
  if (!summary || summary.commissionAmount == null) return null;

  const fee = formatCommissionPercent(summary.upfrontFee);
  const channel = formatCommissionPercent(summary.channelIfastChargePercent);
  const ifast = formatCommissionPercent(summary.ifastCharge);
  const rate = formatCommissionPercent(summary.commissionRate);
  const ifastFactors =
    summary.accountType === "NonWrap"
      ? `${formatCommissionPercent(summary.nonWrapMaxSubscriptionFee)} × ${channel}`
      : `${fee} × ${channel}`;

  return [
    `iFast charge = ${ifastFactors} = ${ifast}`,
    `Commission rate = ${fee} − ${ifast} = ${rate}`,
    `Commission = ${formatGroupedAmount(summary.investmentAmount)} × ${rate} = HK$ ${formatGroupedAmount(summary.commissionAmount)}`,
  ];
};
